import * as React from 'react';
import { Card, CardContent } from './Card';
import { cn } from '../utils';

export interface DocumentListItemSkeletonProps {
  className?: string;
  testId?: string;
  id?: string;
}

/** Placeholder of a `DocumentListItem` row while a document list loads. */
const DocumentListItemSkeleton = React.forwardRef<HTMLDivElement, DocumentListItemSkeletonProps>(
  ({ className, testId, id }, ref) => (
    // A query container like the row, or the two would disagree on layout while loading.
    <Card
      ref={ref}
      id={id}
      testId={testId}
      className={cn('@container/document-row w-full', className)}
    >
      <CardContent className='p-md @2xl/document-row:p-lg'>
        <div className='animate-pulse'>
          {/* Mobile skeleton */}
          <div className='@2xl/document-row:hidden space-y-md'>
            <div className='flex items-start gap-md'>
              <div className='w-10 h-10 bg-background-muted rounded-lg shrink-0' />
              <div className='flex-1 flex items-start justify-between gap-sm'>
                <div className='h-5 w-36 bg-background-disabled rounded' />
                <div className='h-5 w-20 bg-background-disabled rounded' />
              </div>
            </div>
            <div className='h-4 w-40 bg-background-muted rounded' />
            <div className='h-3 w-32 bg-background-muted rounded' />
            <div className='flex items-center gap-sm'>
              <div className='h-3 w-16 bg-background-muted rounded' />
              <div className='h-4 w-20 bg-background-disabled rounded' />
            </div>
            <div className='flex items-center gap-sm'>
              <div className='h-3 w-16 bg-background-muted rounded' />
              <div className='h-4 w-20 bg-background-disabled rounded' />
            </div>
            <div className='flex items-center justify-between'>
              <div className='h-5 w-28 bg-background-disabled rounded' />
              <div className='h-6 w-24 bg-background-muted rounded-lg' />
            </div>
          </div>

          {/* Desktop skeleton — mirrors the desktop columns */}
          <div className='hidden @2xl/document-row:flex items-center gap-md @4xl/document-row:gap-lg w-full overflow-hidden'>
            {/* Col 1 – icon */}
            <div className='w-10 h-10 bg-background-muted rounded-lg shrink-0' />

            {/* Col 2 – number + type */}
            <div className='min-w-0 flex-1 max-w-44 @4xl/document-row:max-w-52 @5xl/document-row:max-w-80 space-y-sm'>
              <div className='h-5 w-32 bg-background-disabled rounded' />
              <div className='h-3 w-16 bg-background-muted rounded' />
            </div>

            {/* Col 3 – vendor + customer */}
            <div className='min-w-0 flex-1 max-w-36 @4xl/document-row:max-w-44 @5xl/document-row:max-w-52 space-y-sm'>
              <div className='h-4 w-28 bg-background-muted rounded' />
              <div className='h-3 w-24 bg-background-muted rounded' />
            </div>

            {/* Col 4 – dates (appears once the row is wide enough) */}
            <div className='hidden @4xl/document-row:block shrink-0 w-36 @4xl/document-row:w-40 space-y-sm'>
              <div className='h-3 w-32 bg-background-muted rounded' />
              <div className='h-3 w-32 bg-background-muted rounded' />
            </div>

            {/* Col 5 – status badge */}
            <div className='shrink-0'>
              <div className='h-6 w-28 bg-background-muted rounded-lg' />
            </div>

            {/* Col 6 – purpose (appears once the row is wide enough) */}
            <div className='hidden @5xl/document-row:block flex-1 min-w-0'>
              <div className='h-4 w-20 bg-background-muted rounded' />
            </div>

            {/* Col 7 – amount */}
            <div className='ml-auto shrink-0 flex flex-col items-end gap-sm'>
              <div className='h-5 w-32 bg-background-disabled rounded' />
              <div className='h-3 w-20 bg-background-muted rounded' />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
);
DocumentListItemSkeleton.displayName = 'DocumentListItemSkeleton';

export { DocumentListItemSkeleton };
